import {
    DiagnosisTime,
    DiagnosisTimeMap,
    GoodNeutralBadNa,
    type YesNoNa,
    YesNoNaOptions,
    type YesNoPreferNotToSay,
    YesNoPreferNotToSayOptions,
} from "@qendo/survey/fields";
import { type AgeRange, AgeRangeMap, AgeRangeOptions, Gender, GenderMap } from "@qendo/types";
import type { DateArg } from "date-fns";
import { useEffect, useState } from "react";
import { Text } from "react-native";
import { CheckboxInput } from "~/components/form/CheckboxInput";
import { ComboBox } from "~/components/form/combo-box/ComboBox";
import { DatePicker } from "~/components/form/date-picker/DatePicker";
import { FieldGroup } from "~/components/form/FieldGroup";
import { FieldLabel } from "~/components/form/FieldLabel";
import { RadioSelector } from "~/components/form/radio/RadioSelector";
import { TextInput } from "~/components/form/TextInput";
import { BodyText, Em } from "~/components/layout/text";
import { OnboardingSurveyData } from "~/entities/survey/onboarding-survey-data";
import { useOnboarding } from "~/providers/OnboardingProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { filled } from "~/util";
import { filterUnique } from "~/util/array";
import type { DataProperty } from "~/util/data";

const other = "";
type Other = typeof other;

export type OnboardingSurveyProps = {
    value?: OnboardingSurveyData | null;
    setPayload: (payload: OnboardingSurveyData) => void;
    disabled?: boolean;
};

const diagnosis_options = [
    "Endometriosis",
    "PCOS",
    "Adenomyosis",
    "Chronic pelvic pain (undiagnosed)",
    "Suspected endometriosis",
    "I’m supporting someone with one of these conditions",
    "Prefer not to say",
];

const main_symptoms = [
    "Pelvic pain",
    "Period pain",
    "Pain with sex",
    "Bowel symptoms (e.g. diarrhoea, constipation, pain)",
    "Bladder symptoms (e.g. urgency, pain, frequency)",
    "Fatigue",
    "Infertility or fertility concerns",
    "Mental health impacts (e.g. anxiety, low mood, overwhelm)",
];

export const OnboardingSurvey = ({ value, setPayload, disabled }: OnboardingSurveyProps) => {
    const current = value ?? new OnboardingSurveyData();
    const { visible } = useOnboarding();
    const [informationLanguage, setInformationLanguage] = useState(false);
    const [otherSymptom, setOtherSymptom] = useState(false);
    const [otherSymptomValue, setOtherSymptomValue] = useState("");
    const { profile, is_primary } = useProfile();

    const updateValue = <K extends DataProperty<OnboardingSurveyData>>(field: K, value: OnboardingSurveyData[K]) => {
        const payload = OnboardingSurveyData.from(current);

        payload[field] = value;

        if (payload.dirty(field)) {
            setPayload(payload);
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies(current): initial load
    // biome-ignore lint/correctness/useExhaustiveDependencies(setPayload): initial load
    useEffect(() => {
        const payload = OnboardingSurveyData.from(current);

        payload.language = payload.language ?? "English";

        if (is_primary) {
            payload.gender = profile.gender ?? null;
            payload.postcode = profile.postcode ?? "";
            payload.age_range = profile.age_range ?? null;
        }

        if (payload.dirty(["language", "gender", "postcode", "age_range"])) {
            setPayload(payload);
        }
    }, [is_primary, profile]);

    return (
        <FieldGroup>
            <TextInput
                label="Where do you live?"
                hint="Share your postcode"
                name="postcode"
                value={current.postcode}
                onChangeText={(value: string) => updateValue("postcode", value)}
                disabled={disabled}
            />
            <ComboBox<AgeRange>
                name="age_range"
                items={AgeRangeOptions}
                itemMap={AgeRangeMap}
                value={current.age_range}
                onItemSelected={(value: AgeRange) => updateValue("age_range", value)}
                label="What is your age group?"
                disabled={disabled}
            />
            <DatePicker
                label="What is your date of birth?"
                name="date_of_birth"
                hint="This helps us tailor content to your life stage."
                value={current.date_of_birth}
                onDateSelected={(value: DateArg<Date> | null) => updateValue("date_of_birth", value)}
                disabled={disabled}
            />
            <ComboBox<Gender | Other>
                label="What is your gender identity?"
                items={[...Object.values(Gender), other]}
                itemMap={{ ...GenderMap, [other]: "Prefer to self describe" }}
                value={current.gender}
                onItemSelected={(value: Gender | Other | null) => updateValue("gender", value)}
                disabled={disabled}
            />
            {current.gender === other && (
                <TextInput
                    name="gender_description"
                    value={current.gender_description}
                    onChangeText={(value: string) => updateValue("gender_description", value)}
                    disabled={disabled}
                />
            )}
            <RadioSelector<YesNoPreferNotToSay>
                title="Do you identify as Aboriginal and/or Torres Strait Islander?"
                options={YesNoPreferNotToSayOptions}
                name="aboriginal_or_torres_straight_islander"
                value={current.aboriginal_or_torres_straight_islander}
                onValueChange={(value: YesNoPreferNotToSay) =>
                    updateValue("aboriginal_or_torres_straight_islander", value)
                }
                disabled={disabled}
            />
            <TextInput
                label="What language do you speak most often at home?"
                name="language"
                value={current.language}
                onChangeText={(value: string) => updateValue("language", value)}
                disabled={disabled}
            />
            <CheckboxInput
                label="Would you like information in a language other than English?"
                value={informationLanguage}
                disabled={disabled}
                onValueChange={(value: boolean) => {
                    setInformationLanguage(value);
                    value || updateValue("information_language", "");
                }}
            />
            {informationLanguage && (
                <TextInput
                    label="Language"
                    name="information_language"
                    value={current.information_language}
                    onChangeText={(value: string) => updateValue("information_language", value)}
                    disabled={disabled}
                />
            )}
            <RadioSelector<YesNoPreferNotToSay>
                title="Do you have a disability or long-term health condition?"
                options={YesNoPreferNotToSayOptions}
                name="disability"
                value={current.disability}
                onValueChange={(value: YesNoPreferNotToSay) => updateValue("disability", value)}
                disabled={disabled}
            />
            <TextInput
                label="What is your Medicare card number?"
                name="medicare"
                value={current.medicare}
                onChangeText={(value: string) => updateValue("medicare", value)}
                disabled={disabled}
                hint={
                    <Text>
                        This is <Em>completely optional</Em>. We ask this only to better understand how people are using
                        the healthcare system. It is stored securely in line with Australian privacy laws.
                    </Text>
                }
                optional
            />
            <FieldLabel label="Do you have a diagnosis of any of the following?" hint="Tick all that apply">
                {diagnosis_options.map((v) => (
                    <CheckboxInput
                        key={v}
                        label={v}
                        value={current.diagnosis.includes(v)}
                        onValueChange={(selected) => {
                            updateValue(
                                "diagnosis",
                                selected
                                    ? [...current.diagnosis, v].filter(filterUnique)
                                    : current.diagnosis.filter((d) => d !== v),
                            );
                        }}
                    />
                ))}
            </FieldLabel>
            <ComboBox<DiagnosisTime>
                label="How long did it take you to receive a diagnosis (if applicable)?"
                name="diagnosis_time"
                items={Object.values(DiagnosisTime)}
                itemMap={DiagnosisTimeMap}
                value={current.diagnosis_time}
                onItemSelected={(value: DiagnosisTime | null) => updateValue("diagnosis_time", value)}
                disabled={disabled}
                clearable
            />
            <FieldLabel label="What are your main symptoms right now?">
                {main_symptoms.map((v) => (
                    <CheckboxInput
                        key={v}
                        label={v}
                        value={current.symptoms.includes(v)}
                        disabled={disabled}
                        onValueChange={(selected) => {
                            updateValue(
                                "symptoms",
                                selected
                                    ? [...current.symptoms, v].filter(filterUnique)
                                    : current.symptoms.filter((d) => d !== v),
                            );
                        }}
                    />
                ))}
                <CheckboxInput
                    label="Other"
                    value={otherSymptom}
                    disabled={disabled}
                    onValueChange={(selected) => {
                        setOtherSymptom(selected);
                        updateValue(
                            "symptoms",
                            selected
                                ? [...current.symptoms, otherSymptomValue].filter(filterUnique).filter(filled)
                                : current.symptoms.filter((o) => main_symptoms.includes(o)),
                        );
                    }}
                />
                {otherSymptom && (
                    <TextInput
                        value={otherSymptomValue}
                        disabled={disabled}
                        onChangeText={(v) => {
                            updateValue(
                                "symptoms",
                                [...current.symptoms.filter((o) => main_symptoms.includes(o)), v]
                                    .filter(filterUnique)
                                    .filter(filled),
                            );
                            setOtherSymptomValue(v);
                        }}
                    />
                )}
            </FieldLabel>

            <RadioSelector<YesNoNa>
                title="Have you had any unplanned visits to a Public Hospital, Emergency or specialist recently?"
                options={YesNoNaOptions}
                name="unplanned_visits"
                value={current.unplanned_visits}
                onValueChange={(value: YesNoNa) => updateValue("unplanned_visits", value)}
                disabled={disabled}
            />
            <RadioSelector<GoodNeutralBadNa>
                title="How often are you currently seeing your GP about your symptoms or condition?"
                options={[
                    { value: GoodNeutralBadNa.good, label: "More often than I’d like" },
                    { value: GoodNeutralBadNa.neutral, label: "About as often as I need" },
                    { value: GoodNeutralBadNa.bad, label: "Less often than I’d like" },
                    { value: GoodNeutralBadNa.na, label: "I don’t currently see a GP about this" },
                ]}
                name="visiting_gp"
                value={current.visiting_gp}
                onValueChange={(value: GoodNeutralBadNa) => updateValue("visiting_gp", value)}
                disabled={disabled}
            />
            <RadioSelector<GoodNeutralBadNa>
                title="If you are employed, how supported do you feel at work with your symptoms or condition?"
                options={[
                    { value: GoodNeutralBadNa.good, label: "I feel well supported" },
                    { value: GoodNeutralBadNa.neutral, label: "I have some tools, but I need more support" },
                    { value: GoodNeutralBadNa.bad, label: "I don’t feel well supported at work" },
                    {
                        value: GoodNeutralBadNa.na,
                        label: "Not applicable (not currently employed / student / carer etc.)",
                    },
                ]}
                name="employment_tools"
                value={current.employment_tools}
                onValueChange={(value: GoodNeutralBadNa) => updateValue("employment_tools", value)}
                disabled={disabled}
            />
            <RadioSelector<GoodNeutralBadNa>
                title="Right now, how would you describe your level of movement or physical activity?"
                options={[
                    {
                        value: GoodNeutralBadNa.good,
                        label: "I’m moving my body regularly in a way that works for me",
                    },
                    {
                        value: GoodNeutralBadNa.neutral,
                        label: "I move sometimes, but pain or fatigue often gets in the way",
                    },
                    { value: GoodNeutralBadNa.bad, label: "I rarely feel able to move my body" },
                    { value: GoodNeutralBadNa.na, label: "Not sure" },
                ]}
                name="movement"
                value={current.movement}
                onValueChange={(value: GoodNeutralBadNa) => updateValue("movement", value)}
                disabled={disabled}
            />
            <RadioSelector<GoodNeutralBadNa>
                title="Right now, how would you describe your eating habits?"
                options={[
                    { value: GoodNeutralBadNa.good, label: "My eating feels mostly balanced and works for me" },
                    { value: GoodNeutralBadNa.neutral, label: "My eating is okay, but symptoms often affect it" },
                    {
                        value: GoodNeutralBadNa.bad,
                        label: "My eating habits feel difficult to manage at the moment",
                    },
                    { value: GoodNeutralBadNa.na, label: "Not sure" },
                ]}
                name="eating_habits"
                value={current.eating_habits}
                onValueChange={(value: GoodNeutralBadNa) => updateValue("eating_habits", value)}
                disabled={disabled}
            />
            <RadioSelector<GoodNeutralBadNa>
                title="Right now, how would you describe your mental health?"
                options={[
                    { value: GoodNeutralBadNa.good, label: "I’m generally coping well" },
                    { value: GoodNeutralBadNa.neutral, label: "I’m managing, but it feels hard a lot of the time" },
                    { value: GoodNeutralBadNa.bad, label: "I’m really struggling with my mental health" },
                    { value: GoodNeutralBadNa.na, label: "Not sure / Prefer not to say" },
                ]}
                name="mental_health"
                value={current.mental_health}
                onValueChange={(value: GoodNeutralBadNa) => updateValue("mental_health", value)}
                disabled={disabled}
            />
            <RadioSelector<GoodNeutralBadNa>
                title="How are you currently managing your day-to-day activities (study, work, parenting, caring, life admin)?"
                options={[
                    { value: GoodNeutralBadNa.good, label: "I can mostly do what I need to do" },
                    { value: GoodNeutralBadNa.neutral, label: "I can manage, but I often need to pace or adjust" },
                    { value: GoodNeutralBadNa.bad, label: "It’s very hard to keep up with day-to-day activities" },
                    { value: GoodNeutralBadNa.na, label: "Not sure" },
                ]}
                name="daily_activities"
                value={current.daily_activities}
                onValueChange={(value: GoodNeutralBadNa) => updateValue("daily_activities", value)}
                disabled={disabled}
            />
            {visible("cycle") && (
                <BodyText size="md">Next, let’s set up your cycle tracking (this is optional)</BodyText>
            )}
        </FieldGroup>
    );
};
